import React, { createContext, useContext, useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

export const CartContext = createContext();

export function CartProvider({ children }) {
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load from localStorage (guest) OR Firestore (user) on auth change
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(true);
      if (firebaseUser) {
        // 1. Load user's Firestore cart
        const cartRef = doc(db, "carts", firebaseUser.uid);
        const snap = await getDoc(cartRef);

        // 2. Merge localStorage guest cart if exists
        const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
        let userCart = snap.exists() ? snap.data().items : [];
        if (guestCart.length > 0) {
          // Merge: Add any unique guest items not in Firestore
          const mergedCart = [
            ...userCart,
            ...guestCart.filter(
              (g) => !userCart.some((u) => u.id === g.id)
            ),
          ];
          setCart(mergedCart);
          // Update Firestore with merged cart
          await setDoc(cartRef, { items: mergedCart });
          localStorage.removeItem("cart");
        } else {
          setCart(userCart);
        }
      } else {
        // Not logged in: load from localStorage (or empty)
        const guestCart = JSON.parse(localStorage.getItem("cart") || "[]");
        setCart(guestCart);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Always save cart to localStorage (for guests)
  useEffect(() => {
    if (!user && !loading) {
      localStorage.setItem("cart", JSON.stringify(cart));
    }
  }, [cart, user, loading]);

  // Save to Firestore whenever cart changes and logged in
  useEffect(() => {
    if (user && !loading) {
      const saveCart = async () => {
        const cartRef = doc(db, "carts", user.uid);
        await setDoc(cartRef, { items: cart });
      };
      saveCart();
    }
  }, [cart, user, loading]);

  const addToCart = (item) =>
    setCart((prev) => prev.some((i) => i.id === item.id) ? prev : [...prev, item]);
  const removeFromCart = (id) =>
    setCart((prev) => prev.filter((i) => i.id !== id));
  const clearCart = () => setCart([]);

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, loading }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
